import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { DataTableProps } from './types';
import BulkActions from './BulkActions';

const DataTable = <T extends Record<string, any>>({
  instanceId,
  columns,
  data,
  isLoading = false,
  exportExcel = false,
  exportPDF = false,
  emptyState,
  className = '',
  pageSize = 10,
  pageSizeOptions,
  searchable = true,
  pagination = true,
  serverSidePagination = false,
  totalCount: serverTotalCount = 0,
  page: controlledPage,
  onPageChange,
  onRowClick,
  rowClassName,
  selectable = false,
  onSelectionChange,
  onClearSelection,
  bulkActions = [],
  actions = [],
  actionsColumnLabel = 'Actions',
}: DataTableProps<T>) => {
  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(pageSize);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Gérer la sélection (déclaré tôt pour éviter les erreurs de portée dans les useEffect)
  const getRowId = useCallback((row: T) => {
    return row.id || row.idAlerte || row.idEquipe || JSON.stringify(row);
  }, []);

  // États de sélection persistante
  const [selectedIds, setSelectedIds] = useState<Set<any>>(new Set());

  useEffect(() => {
    setInternalPageSize((prev: number) => (pageSizeOptions?.includes(pageSize) ? pageSize : prev));
  }, [pageSize, pageSizeOptions]);

  const currentPage = serverSidePagination && controlledPage !== undefined ? controlledPage : internalPage;
  const effectivePageSize = serverSidePagination ? pageSize : (pageSizeOptions ? internalPageSize : pageSize);
  const setCurrentPage = useCallback(
    (pageOrUpdater: number | ((p: number) => number)) => {
      if (serverSidePagination && onPageChange) {
        const next = typeof pageOrUpdater === 'function' ? pageOrUpdater(currentPage) : pageOrUpdater;
        onPageChange(next, effectivePageSize);
      } else {
        setInternalPage(typeof pageOrUpdater === 'function' ? pageOrUpdater(internalPage) : pageOrUpdater);
      }
    },
    [serverSidePagination, onPageChange, effectivePageSize, currentPage, internalPage]
  );

  const handlePageSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSize = Number(e.target.value);
      if (Number.isNaN(newSize) || newSize < 1) return;
      if (serverSidePagination && onPageChange) {
        onPageChange(1, newSize);
      } else {
        setInternalPageSize(newSize);
        setInternalPage(1);
      }
    },
    [serverSidePagination, onPageChange]
  );



  const totalCount = serverSidePagination ? serverTotalCount : data.length;

  useEffect(() => {
    if (selectable && onSelectionChange) {
      const selectedRows = data.filter(row => selectedIds.has(getRowId(row)));
      onSelectionChange(selectedRows);
    }
  }, [selectedIds, data, selectable, onSelectionChange, getRowId]);

  const handleSelectAllPage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const pageIds = data.map(row => getRowId(row));
      setSelectedIds(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.add(id));
        return next;
      });
    } else {
      setSelectedIds(new Set());
      if (onClearSelection) onClearSelection();
    }
  }, [data, getRowId, onClearSelection]);

  const handleSelectRow = useCallback((row: T) => {
    const id = getRowId(row);
    setSelectedIds((prev: Set<any>) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [getRowId]);


  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onClearSelection) onClearSelection();
  }, [onClearSelection]);

  const currentPageIds = data.map(row => getRowId(row));
  const selectedOnPage = currentPageIds.filter(id => selectedIds.has(id));

  const isAllPageSelected = data.length > 0 && selectedOnPage.length === data.length;
  const isSomePageSelected = selectedOnPage.length > 0 && selectedOnPage.length < data.length;

  const currentSelectedCount = selectedIds.size;

  // Filtrer les colonnes visibles
  const visibleColumns = useMemo(() => {
    let cols = columns.filter(col => col.visible !== false);
    // Ajouter automatiquement la colonne Actions si des actions sont fournies
    if (actions.length > 0) {
      const hasActionsColumn = cols.some(col => col.key === 'actions');
      if (!hasActionsColumn) {
        cols.push({
          key: 'actions',
          label: actionsColumnLabel,
          sortable: false,
          visible: true,
          className: 'text-end',
          width: 'auto',
        });
      }
    }
    return cols;
  }, [columns, actions, actionsColumnLabel]);

  // Fonction de recherche
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter(row => {
      return visibleColumns.some(col => {
        if (col.key === 'actions') return false; // Ne pas rechercher dans la colonne actions
        const value = row[col.key];
        const searchValue = String(value || '').toLowerCase();
        return searchValue.includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, visibleColumns]);

  // Fonction de tri
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === bValue) return 0;

      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  // Pagination : côté client (slice) ou côté serveur (data = page courante)
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    if (serverSidePagination) return sortedData;
    const startIndex = (currentPage - 1) * effectivePageSize;
    const endIndex = startIndex + effectivePageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, effectivePageSize, pagination, serverSidePagination]);

  const totalPages = serverSidePagination
    ? Math.max(1, Math.ceil(totalCount / effectivePageSize))
    : Math.ceil(sortedData.length / effectivePageSize);

  const handleSort = useCallback((key: string) => {
    if (key === 'actions') return; // Ne pas trier la colonne actions
    const column = visibleColumns.find(col => col.key === key);
    if (!column || column.sortable === false) return;

    setSortConfig(prev => {
      if (prev?.key === key) {
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        return null;
      }
      return { key, direction: 'asc' };
    });
  }, [visibleColumns]);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    },
    [totalPages, setCurrentPage]
  );

  if (isLoading) {
    return (
      <div className={`data-table-wrapper ${className}`}>
        <div className="data-table-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3 text-muted">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`data-table-wrapper ${className}`}>
        {emptyState || (
          <div className="data-table-empty">
            <p className="text-muted">Aucune donnée disponible</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`data-table-wrapper ${className}`}>
      <div className="data-table-container">
        {/* Barre de recherche + pagination (sélecteur et affichage en haut à droite) */}
        {(searchable || pagination) && (
          <div className="data-table-search mb-3">
            <div className="row align-items-center">
              <div className="col-md-6">
                {searchable && (
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="ri-search-line"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="col-md-6 text-md-end mt-2 mt-md-0 d-flex align-items-center justify-content-md-end gap-2 flex-wrap">
                {pagination && (
                  <>
                    {pageSizeOptions && pageSizeOptions.length > 0 && (
                      <label className="d-flex align-items-center gap-1 mb-0">
                        <span className="text-muted small">Afficher</span>
                        <select
                          className="form-select form-select-sm"
                          value={effectivePageSize}
                          onChange={handlePageSizeChange}
                          style={{ width: 'auto' }}
                          aria-label="Nombre d'éléments par page"
                        >
                          {pageSizeOptions.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                        <span className="text-muted small">par page</span>
                      </label>
                    )}
                    <span className="text-muted small">
                      Affichage de {(currentPage - 1) * effectivePageSize + 1} à{' '}
                      {serverSidePagination
                        ? Math.min(currentPage * effectivePageSize, totalCount)
                        : Math.min(currentPage * effectivePageSize, sortedData.length)}{' '}
                      sur {serverSidePagination ? totalCount : sortedData.length} entrées
                    </span>
                  </>
                )}
                {searchable && !pagination && (
                  <span className="text-muted">
                    {sortedData.length} résultat{sortedData.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tableau */}
        <div className="table-responsive">
          <table
            id={instanceId}
            className="table table-bordered table-striped table-hover data-table-modern"
          >
            <thead>
              <tr>
                {selectable && (
                  <th style={{ width: '40px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <div className="form-check d-flex justify-content-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isAllPageSelected}
                        ref={el => {
                          if (el) el.indeterminate = isSomePageSelected;
                        }}
                        onChange={handleSelectAllPage}
                      />
                    </div>
                  </th>
                )}
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`${col.headerClassName || ''} ${col.sortable !== false && col.key !== 'actions' ? 'sortable' : ''
                      } ${sortConfig?.key === col.key
                        ? `sort-${sortConfig.direction}`
                        : ''
                      }`}
                    style={{ width: col.width, padding: '0.5rem', verticalAlign: 'middle' }}
                    onClick={() => handleSort(col.key)}
                  >
                    <div className="th-content">
                      {col.label}
                      {col.sortable !== false && col.key !== 'actions' && (
                        <span className="sort-indicator">
                          {sortConfig?.key === col.key ? (
                            sortConfig.direction === 'asc' ? (
                              <i className="ri-arrow-up-line"></i>
                            ) : (
                              <i className="ri-arrow-down-line"></i>
                            )
                          ) : (
                            <i className="ri-arrow-up-down-line"></i>
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, rowIndex) => {
                const actualIndex = pagination
                  ? (currentPage - 1) * effectivePageSize + rowIndex
                  : rowIndex;
                const customRowClass = rowClassName
                  ? rowClassName(row, actualIndex)
                  : '';

                return (
                  <tr
                    key={actualIndex}
                    className={customRowClass}
                    onClick={() => onRowClick?.(row, actualIndex)}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {selectable && (
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <div className="form-check d-flex justify-content-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedIds.has(getRowId(row))}
                            onChange={() => handleSelectRow(row)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </td>
                    )}
                    {visibleColumns.map((col) => {
                      // Colonne Actions avec boutons horizontaux
                      if (col.key === 'actions' && actions.length > 0) {
                        // Filtrer les actions visibles
                        const visibleActions = actions.filter(action => {
                          if (action.visible === undefined) return true;
                          return action.visible(row);
                        });

                        return (
                          <td
                            key={col.key}
                            className={`${col.className || ''} actions-cell`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ padding: '0.5rem', verticalAlign: 'middle' }}
                          >
                            <div className="d-flex gap-2 justify-content-end align-items-center">
                              {visibleActions.map((action, idx) => {
                                const isDisabled = action.disabled ? action.disabled(row) : false;
                                const variantClass = action.variant ? `btn-${action.variant}` : 'btn-secondary';

                                return (
                                  <button
                                    key={idx}
                                    className={`btn btn-sm ${variantClass}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isDisabled) {
                                        action.onClick(row, actualIndex);
                                      }
                                    }}
                                    disabled={isDisabled}
                                    title={action.label}
                                  >
                                    {action.icon && <i className={action.icon}></i>}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        );
                      }

                      // Colonnes normales
                      const value = row[col.key];
                      const cellContent = col.render
                        ? col.render(value, row, actualIndex)
                        : value ?? '';

                      return (
                        <td
                          key={col.key}
                          className={col.className || ''}
                          style={{ padding: '0.5rem', verticalAlign: 'middle' }}
                        >
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (totalPages > 1 || (pageSizeOptions && pageSizeOptions.length > 0)) && (
          <div className="data-table-pagination mt-3">
            <div className="row align-items-center">
              <div className="col-md-6 d-flex align-items-center gap-2 flex-wrap">
                {pageSizeOptions && pageSizeOptions.length > 0 && (
                  <label className="d-flex align-items-center gap-1 mb-0">
                    <span className="text-muted small">Afficher</span>
                    <select
                      className="form-select form-select-sm"
                      value={effectivePageSize}
                      onChange={handlePageSizeChange}
                      style={{ width: 'auto' }}
                      aria-label="Nombre d'éléments par page"
                    >
                      {pageSizeOptions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <span className="text-muted small">par page</span>
                  </label>
                )}
                {totalPages > 1 && (
                  <span className="text-muted">
                    Affichage de {(currentPage - 1) * effectivePageSize + 1} à{' '}
                    {serverSidePagination
                      ? Math.min(currentPage * effectivePageSize, serverTotalCount)
                      : Math.min(currentPage * effectivePageSize, sortedData.length)}{' '}
                    sur {serverSidePagination ? serverTotalCount : sortedData.length} entrées
                  </span>
                )}
              </div>
              <div className="col-md-6">
                {totalPages > 1 && (
                  <nav>
                    <ul className="pagination justify-content-end mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                        >
                          <i className="ri-arrow-left-double-line"></i>
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="ri-arrow-left-line"></i>
                        </button>
                      </li>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <i className="ri-arrow-right-line"></i>
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          <i className="ri-arrow-right-double-line"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions floating bar */}
        {selectable && currentSelectedCount > 0 && (
          <BulkActions
            selectedCount={currentSelectedCount}
            actions={bulkActions}
            onClear={handleClearSelection}
          />
        )}
      </div>
    </div>
  );
};

export default DataTable;
